import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import frCommon from '../locales/fr/common.json'
import frBetternotes from '../locales/fr/betternotes.json'
import frAuth from '../locales/fr/auth.json'
import frComponents from '../locales/fr/components.json'
import frAdmin from '../locales/fr/admin.json'
import frApp from '../locales/fr/app.json'
import frCrm from '../locales/fr/crm.json'
import frOnboarding from '../locales/fr/onboarding.json'
import frProfile from '../locales/fr/profile.json'
import frShop from '../locales/fr/shop.json'
import frIntegrations from '../locales/fr/integrations.json'
import frResults from '../locales/fr/results.json'
import frResult from '../locales/fr/result.json'
import frMarketing from '../locales/fr/marketing.json'
import frLegal from '../locales/fr/legal.json'
import frTour from '../locales/fr/tour.json'
import enCommon from '../locales/en/common.json'
import enBetternotes from '../locales/en/betternotes.json'
import enAuth from '../locales/en/auth.json'
import enComponents from '../locales/en/components.json'
import enAdmin from '../locales/en/admin.json'
import enApp from '../locales/en/app.json'
import enCrm from '../locales/en/crm.json'
import enOnboarding from '../locales/en/onboarding.json'
import enProfile from '../locales/en/profile.json'
import enShop from '../locales/en/shop.json'
import enIntegrations from '../locales/en/integrations.json'
import enResults from '../locales/en/results.json'
import enResult from '../locales/en/result.json'
import enMarketing from '../locales/en/marketing.json'
import enLegal from '../locales/en/legal.json'
import enTour from '../locales/en/tour.json'

export const SUPPORTED_LOCALES = ['fr', 'en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'fr',
    supportedLngs: SUPPORTED_LOCALES,
    ns: [
      'common',
      'betternotes',
      'auth',
      'components',
      'admin',
      'app',
      'crm',
      'onboarding',
      'profile',
      'shop',
      'integrations',
      'results',
      'result',
      'marketing',
      'legal',
      'tour',
    ],
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    // i18next-browser-languagedetector reads + writes localStorage under
    // the key `i18nextLng` by default. The user-facing locale switcher in
    // the Navbar calls i18n.changeLanguage(lng) and the choice survives
    // page reloads + sessions.
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    resources: {
      fr: {
        common: frCommon,
        betternotes: frBetternotes,
        auth: frAuth,
        components: frComponents,
        admin: frAdmin,
        app: frApp,
        crm: frCrm,
        onboarding: frOnboarding,
        profile: frProfile,
        shop: frShop,
        integrations: frIntegrations,
        results: frResults,
        result: frResult,
        marketing: frMarketing,
        legal: frLegal,
        tour: frTour,
      },
      en: {
        common: enCommon,
        betternotes: enBetternotes,
        auth: enAuth,
        components: enComponents,
        admin: enAdmin,
        app: enApp,
        crm: enCrm,
        onboarding: enOnboarding,
        profile: enProfile,
        shop: enShop,
        integrations: enIntegrations,
        results: enResults,
        result: enResult,
        marketing: enMarketing,
        legal: enLegal,
        tour: enTour,
      },
    },
  })

// Keep `<html lang>` in sync with the active locale — matters for screen
// readers (correct pronunciation) and SEO (Google uses it as a strong
// signal for the page language). i18next-browser-languagedetector only
// reads localStorage / navigator; it doesn't write the document attribute.
if (typeof document !== 'undefined') {
  const syncHtmlLang = (lng: string) => {
    const short = lng.split('-')[0]
    if (document.documentElement.lang !== short) {
      document.documentElement.lang = short
    }
  }
  syncHtmlLang(i18n.language || 'fr')
  i18n.on('languageChanged', syncHtmlLang)
}

export default i18n
